/**
 * Tiny line-level diff.
 *
 * Computes the longest-common-subsequence of two strings (split by
 * newline) and emits a list of ops: `eq` (line unchanged), `del` (line
 * only in the left / previous version), `add` (line only in the right /
 * newer version). Good enough for authored compliance docs which are
 * rarely >200 lines — LCS is O(n·m) memory which is fine at that scale.
 *
 * Returns rows shaped for side-by-side rendering:
 *   [{ type: 'eq'|'del'|'add', left: string|null, right: string|null }]
 */
export const lineDiff = (a, b) => {
  const A = (a || '').split('\n');
  const B = (b || '').split('\n');
  const n = A.length;
  const m = B.length;
  // Allocate LCS DP table. Sentinel row/col of zeros on the high edge.
  const dp = Array.from({ length: n + 1 }, () => new Uint32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = A[i] === B[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) {
      out.push({ type: 'eq', left: A[i], right: B[j] });
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: 'del', left: A[i], right: null });
      i++;
    } else {
      out.push({ type: 'add', left: null, right: B[j] });
      j++;
    }
  }
  while (i < n) { out.push({ type: 'del', left: A[i++], right: null }); }
  while (j < m) { out.push({ type: 'add', left: null, right: B[j++] }); }
  return out;
};
